import { ValidationError } from '../../../shared/exceptions/validation.error';
import { filenameFromResponse } from '../../../shared/utils/download-blob.util';
import { BaseService } from '../../base';
import type {
  RdAggregateProducts,
  RdCatalogEquipment,
  RdClientPayload,
  RdCommercialRule,
  RdCoverageDef,
  RdCoverageSelectionPayload,
  RdEquipmentPayload,
  RdFleetPayload,
  RdFranchiseRule,
  RdIndemnityPeriod,
  RdFrotaPlano,
  RdInstallmentConfig,
  RdModality,
  RdModalitySlug,
  RdParamsPayload,
  RdPaymentMethod,
  RdProposal,
  RdQuoteResult,
  RdTransmission,
  RdModalityAgeBands,
  RdModalityCoverages,
} from './types';

const PORTAL_API_URL = import.meta.env.VITE_API_URL as string | undefined;
const BASE_PATH = '/portal/v1/rd-equipamentos/quotes';

export class RdEquipamentosService extends BaseService {
  constructor() {
    super();
  }

  /**
   * RD errors arrive as HTTP 400 (RFC 9457 `problem+json`). Two shapes:
   * - Field-level: `{ status: 400, errors: Record<string, string[]> }`
   *   (`ProblemDetailsException.validationError`). Maps each entry to
   *   `ValidationError.fieldErrors[key] = { code: '', message: messages[0] }`.
   * - Business-rule: `{ status: 400, detail: string }`, no `errors` key at
   *   all (`ProblemDetailsException.badRequest`) — e.g. "Acima de 5 itens
   *   segue para subscrição manual." or "Modalidade exige CEP/endereço do
   *   risco.".
   * Both are surfaced as `ValidationError` so callers have a single,
   * reliable type to branch on: `fieldErrors` holds per-field messages and
   * is left `undefined` (not `{}`) when the response had no `errors` map,
   * so callers can reliably branch on `if (error.fieldErrors)`. `detail`
   * holds the business-rule message verbatim (undefined when the response
   * had no `detail` string). When there are no field messages, `.message`
   * is also set to `detail` so code that reads the plain `Error.message`
   * (instead of `.detail`) still gets the real backend text instead of the
   * generic "Erro de validação" fallback.
   * Returns `null` when the response is a 400 with neither `errors` nor a
   * `detail` string, leaving the response body untouched (via `clone()`) so
   * the caller can still fall through to `handleJsonResponse`.
   */
  private async extractValidationError(
    response: Response
  ): Promise<ValidationError | null> {
    if (response.status !== 400) return null;

    let body: unknown;
    try {
      body = await response.clone().json();
    } catch {
      return null;
    }

    if (!body || typeof body !== 'object') return null;

    const bodyObj = body as { errors?: unknown; detail?: unknown };
    const errors = bodyObj.errors;
    const hasFieldErrors =
      !!errors && typeof errors === 'object' && !Array.isArray(errors);
    const detail =
      typeof bodyObj.detail === 'string' && bodyObj.detail.trim()
        ? bodyObj.detail.trim()
        : undefined;

    if (!hasFieldErrors && !detail) return null;

    const messages: string[] = [];
    let fieldErrors:
      | Record<string, { code: string; message: string }>
      | undefined;

    if (hasFieldErrors) {
      fieldErrors = {};
      for (const [field, value] of Object.entries(
        errors as Record<string, unknown>
      )) {
        const message = Array.isArray(value)
          ? String(value[0] ?? '').trim()
          : '';
        fieldErrors[field] = { code: '', message };
        if (message) messages.push(message);
      }
    }

    const validationError = new ValidationError(messages);
    validationError.fieldErrors = fieldErrors;
    if (detail) {
      validationError.detail = detail;
      if (messages.length === 0) {
        validationError.message = detail;
      }
    }
    return validationError;
  }

  async createProposal(
    payload: RdClientPayload,
    partyCode?: string
  ): Promise<{
    success: boolean;
    data?: RdProposal;
    error?: string;
    message?: string;
  }> {
    try {
      // Perfil interno (seguradora) cria em nome da corretora selecionada no
      // topo — o BFF exige o dono (partyCode) para roteá-la ao motor.
      const path = partyCode
        ? `${BASE_PATH}?partyCode=${encodeURIComponent(partyCode)}`
        : BASE_PATH;
      const response = await this.post(path, payload, PORTAL_API_URL);
      const validationError = await this.extractValidationError(response);
      if (validationError) throw validationError;
      return await this.handleJsonResponse<RdProposal>(response);
    } catch (error) {
      if (error instanceof ValidationError) throw error;
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Erro ao criar proposta',
      };
    }
  }

  async getProposal(proposalId: string): Promise<{
    success: boolean;
    data?: RdProposal;
    error?: string;
    message?: string;
  }> {
    try {
      const path = `${BASE_PATH}/${proposalId}`;
      const response = await this.get(path, {}, PORTAL_API_URL);
      return await this.handleJsonResponse<RdProposal>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Erro ao buscar proposta',
      };
    }
  }

  async getQuote(proposalId: string): Promise<{
    success: boolean;
    data?: RdQuoteResult;
    error?: string;
    message?: string;
  }> {
    try {
      const path = `${BASE_PATH}/${proposalId}/quote`;
      const response = await this.get(path, {}, PORTAL_API_URL);
      return await this.handleJsonResponse<RdQuoteResult>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Erro ao buscar cotação',
      };
    }
  }

  async getModalities(): Promise<{
    success: boolean;
    data?: RdModality[];
    error?: string;
    message?: string;
  }> {
    try {
      const path = `${BASE_PATH}/config/modalities`;
      const response = await this.get(path, {}, PORTAL_API_URL);
      return await this.handleJsonResponse<RdModality[]>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Erro ao buscar modalidades',
      };
    }
  }

  async getCoverages(): Promise<{
    success: boolean;
    data?: RdCoverageDef[];
    error?: string;
    message?: string;
  }> {
    try {
      const path = `${BASE_PATH}/config/coverages`;
      const response = await this.get(path, {}, PORTAL_API_URL);
      return await this.handleJsonResponse<RdCoverageDef[]>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Erro ao buscar coberturas',
      };
    }
  }

  async getRateGrid(): Promise<{
    success: boolean;
    data?: RdModalityCoverages;
    error?: string;
    message?: string;
  }> {
    try {
      const path = `${BASE_PATH}/config/rate-grid`;
      const response = await this.get(path, {}, PORTAL_API_URL);
      return await this.handleJsonResponse<RdModalityCoverages>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao buscar tabela de taxas',
      };
    }
  }

  async getAgeBands(): Promise<{
    success: boolean;
    data?: RdModalityAgeBands;
    error?: string;
    message?: string;
  }> {
    try {
      const path = `${BASE_PATH}/config/age-bands`;
      const response = await this.get(path, {}, PORTAL_API_URL);
      return await this.handleJsonResponse<RdModalityAgeBands>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao buscar faixas de agravação por idade',
      };
    }
  }

  async getIndemnityPeriods(): Promise<{
    success: boolean;
    data?: RdIndemnityPeriod[];
    error?: string;
  }> {
    try {
      const path = `${BASE_PATH}/config/indemnity-periods`;
      const response = await this.get(path, {}, PORTAL_API_URL);
      return await this.handleJsonResponse<RdIndemnityPeriod[]>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao buscar os períodos indenitários',
      };
    }
  }

  async getFranchiseRules(): Promise<{
    success: boolean;
    data?: RdFranchiseRule[];
    error?: string;
    message?: string;
  }> {
    try {
      const path = `${BASE_PATH}/config/franchise-rules`;
      const response = await this.get(path, {}, PORTAL_API_URL);
      return await this.handleJsonResponse<RdFranchiseRule[]>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao buscar regras de franquia',
      };
    }
  }

  async getCommercialRule(): Promise<{
    success: boolean;
    data?: RdCommercialRule;
    error?: string;
    message?: string;
  }> {
    try {
      const path = `${BASE_PATH}/config/commercial-rule`;
      const response = await this.get(path, {}, PORTAL_API_URL);
      return await this.handleJsonResponse<RdCommercialRule>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao buscar regra comercial',
      };
    }
  }

  async getInstallmentConfig(): Promise<{
    success: boolean;
    data?: RdInstallmentConfig;
    error?: string;
    message?: string;
  }> {
    try {
      const path = `${BASE_PATH}/config/installment-config`;
      const response = await this.get(path, {}, PORTAL_API_URL);
      return await this.handleJsonResponse<RdInstallmentConfig>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao buscar configuração de parcelamento',
      };
    }
  }

  async getPaymentMethods(): Promise<{
    success: boolean;
    data?: RdPaymentMethod[];
    error?: string;
    message?: string;
  }> {
    try {
      const path = `${BASE_PATH}/config/payment-methods`;
      const response = await this.get(path, {}, PORTAL_API_URL);
      return await this.handleJsonResponse<RdPaymentMethod[]>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao buscar formas de pagamento',
      };
    }
  }

  async getFrotaPlanos(): Promise<{
    success: boolean;
    data?: RdFrotaPlano[];
    error?: string;
    message?: string;
  }> {
    try {
      const path = `${BASE_PATH}/config/frota-planos`;
      const response = await this.get(path, {}, PORTAL_API_URL);
      return await this.handleJsonResponse<RdFrotaPlano[]>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao buscar planos de frota',
      };
    }
  }

  async getAggregateProducts(): Promise<{
    success: boolean;
    data?: RdAggregateProducts;
    error?: string;
    message?: string;
  }> {
    try {
      const path = `${BASE_PATH}/config/aggregate-products`;
      const response = await this.get(path, {}, PORTAL_API_URL);
      return await this.handleJsonResponse<RdAggregateProducts>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao buscar produtos agregados',
      };
    }
  }

  async searchEquipment(
    term: string,
    modality?: RdModalitySlug
  ): Promise<{
    success: boolean;
    data?: RdCatalogEquipment[];
    error?: string;
    message?: string;
  }> {
    try {
      const query = new URLSearchParams({ term });
      if (modality) query.set('modality', modality);

      const path = `${BASE_PATH}/equipment-catalog?${query.toString()}`;
      const response = await this.get(path, {}, PORTAL_API_URL);
      return await this.handleJsonResponse<RdCatalogEquipment[]>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao buscar equipamentos',
      };
    }
  }

  async updateClient(
    proposalId: string,
    payload: RdClientPayload
  ): Promise<{
    success: boolean;
    data?: RdProposal;
    error?: string;
    message?: string;
  }> {
    try {
      const path = `${BASE_PATH}/${proposalId}/client`;
      const response = await this.put(path, payload, PORTAL_API_URL);
      const validationError = await this.extractValidationError(response);
      if (validationError) throw validationError;
      return await this.handleJsonResponse<RdProposal>(response);
    } catch (error) {
      if (error instanceof ValidationError) throw error;
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Erro ao atualizar cliente',
      };
    }
  }

  async setFleet(
    proposalId: string,
    payload: RdFleetPayload
  ): Promise<{
    success: boolean;
    data?: RdProposal;
    error?: string;
    message?: string;
  }> {
    try {
      const path = `${BASE_PATH}/${proposalId}/fleet`;
      const response = await this.put(path, payload, PORTAL_API_URL);
      const validationError = await this.extractValidationError(response);
      if (validationError) throw validationError;
      return await this.handleJsonResponse<RdProposal>(response);
    } catch (error) {
      if (error instanceof ValidationError) throw error;
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao definir frota',
      };
    }
  }

  async addEquipment(
    proposalId: string,
    payload: RdEquipmentPayload
  ): Promise<{
    success: boolean;
    data?: RdProposal;
    error?: string;
    message?: string;
  }> {
    try {
      const path = `${BASE_PATH}/${proposalId}/equipment`;
      const response = await this.post(path, payload, PORTAL_API_URL);
      const validationError = await this.extractValidationError(response);
      if (validationError) throw validationError;
      return await this.handleJsonResponse<RdProposal>(response);
    } catch (error) {
      if (error instanceof ValidationError) throw error;
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao adicionar equipamento',
      };
    }
  }

  async removeEquipment(
    proposalId: string,
    itemId: string
  ): Promise<{
    success: boolean;
    data?: RdProposal;
    error?: string;
    message?: string;
  }> {
    try {
      const path = `${BASE_PATH}/${proposalId}/equipment/${itemId}`;
      const response = await this.delete(path, PORTAL_API_URL);
      return await this.handleJsonResponse<RdProposal>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao remover equipamento',
      };
    }
  }

  async setCoverages(
    proposalId: string,
    itemId: string,
    payload: RdCoverageSelectionPayload
  ): Promise<{
    success: boolean;
    data?: RdProposal;
    error?: string;
    message?: string;
  }> {
    try {
      const path = `${BASE_PATH}/${proposalId}/equipment/${itemId}/coverages`;
      const response = await this.put(path, payload, PORTAL_API_URL);
      const validationError = await this.extractValidationError(response);
      if (validationError) throw validationError;
      return await this.handleJsonResponse<RdProposal>(response);
    } catch (error) {
      if (error instanceof ValidationError) throw error;
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Erro ao definir coberturas',
      };
    }
  }

  async setParams(
    proposalId: string,
    payload: RdParamsPayload
  ): Promise<{
    success: boolean;
    data?: RdProposal;
    error?: string;
    message?: string;
  }> {
    try {
      const path = `${BASE_PATH}/${proposalId}/params`;
      const response = await this.put(path, payload, PORTAL_API_URL);
      const validationError = await this.extractValidationError(response);
      if (validationError) throw validationError;
      return await this.handleJsonResponse<RdProposal>(response);
    } catch (error) {
      if (error instanceof ValidationError) throw error;
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Erro ao definir parâmetros',
      };
    }
  }

  async calculate(proposalId: string): Promise<{
    success: boolean;
    data?: RdProposal;
    error?: string;
    message?: string;
  }> {
    try {
      const path = `${BASE_PATH}/${proposalId}/calculate`;
      const response = await this.post(path, {}, PORTAL_API_URL);
      const validationError = await this.extractValidationError(response);
      if (validationError) throw validationError;
      return await this.handleJsonResponse<RdProposal>(response);
    } catch (error) {
      if (error instanceof ValidationError) throw error;
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Erro ao calcular cotação',
      };
    }
  }

  async approve(proposalId: string): Promise<{
    success: boolean;
    data?: RdProposal;
    error?: string;
    message?: string;
  }> {
    try {
      const path = `${BASE_PATH}/${proposalId}/approve`;
      const response = await this.post(path, {}, PORTAL_API_URL);
      const validationError = await this.extractValidationError(response);
      if (validationError) throw validationError;
      return await this.handleJsonResponse<RdProposal>(response);
    } catch (error) {
      if (error instanceof ValidationError) throw error;
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Erro ao aprovar proposta',
      };
    }
  }

  async accept(proposalId: string): Promise<{
    success: boolean;
    data?: RdProposal;
    error?: string;
    message?: string;
  }> {
    try {
      const path = `${BASE_PATH}/${proposalId}/accept`;
      const response = await this.post(path, {}, PORTAL_API_URL);
      const validationError = await this.extractValidationError(response);
      if (validationError) throw validationError;
      return await this.handleJsonResponse<RdProposal>(response);
    } catch (error) {
      if (error instanceof ValidationError) throw error;
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Erro ao gerar proposta',
      };
    }
  }

  /**
   * Transmite a proposta gerada (`aceita`): agenda a emissão no BFF, que
   * segue o Modo de emissão do produto (manual ou automática no i4Pro).
   */
  async transmit(proposalId: string): Promise<{
    success: boolean;
    data?: RdProposal;
    error?: string;
    message?: string;
  }> {
    try {
      const path = `${BASE_PATH}/${proposalId}/transmit`;
      const response = await this.post(path, {}, PORTAL_API_URL);
      const validationError = await this.extractValidationError(response);
      if (validationError) throw validationError;
      return await this.handleJsonResponse<RdProposal>(response);
    } catch (error) {
      if (error instanceof ValidationError) throw error;
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao transmitir proposta',
      };
    }
  }

  /** Se a proposta já foi transmitida (existe o controle de emissão). */
  async getTransmission(proposalId: string): Promise<{
    success: boolean;
    data?: RdTransmission;
    error?: string;
    message?: string;
  }> {
    try {
      const path = `${BASE_PATH}/${proposalId}/transmission`;
      const response = await this.get(path, {}, PORTAL_API_URL);
      return await this.handleJsonResponse<RdTransmission>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao consultar a transmissão da proposta',
      };
    }
  }

  async cancel(proposalId: string): Promise<{
    success: boolean;
    data?: RdProposal;
    error?: string;
    message?: string;
  }> {
    try {
      const path = `${BASE_PATH}/${proposalId}/cancel`;
      const response = await this.post(path, {}, PORTAL_API_URL);
      const validationError = await this.extractValidationError(response);
      if (validationError) throw validationError;
      return await this.handleJsonResponse<RdProposal>(response);
    } catch (error) {
      if (error instanceof ValidationError) throw error;
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Erro ao cancelar proposta',
      };
    }
  }

  /**
   * PDF da apólice emitida (binário). Mesmo contrato de
   * `policiesService.getStarterKit`: resolve `{blob, filename}` ou lança —
   * o chamador decide como salvar (`downloadBlob`).
   */
  /**
   * Motivo legível de uma resposta de erro, ou vazio quando não há.
   *
   * Aceita as duas formas que o BFF devolve — `detail` (RFC 9457, que é o
   * que o motor produz) e `message` (exceções do Nest) — e nunca lança:
   * corpo ilegível vira string vazia, e quem chama decide o texto padrão.
   *
   * Os PDFs não passam pelo `handleJsonResponse`, então o aviso de corretora
   * inativa (403) sai daqui.
   */
  private async reasonFromResponse(response: Response): Promise<string> {
    let reason = '';
    try {
      const body = (await response.json()) as {
        detail?: unknown;
        message?: unknown;
      };
      for (const campo of [body?.detail, body?.message]) {
        if (typeof campo === 'string' && campo.trim()) {
          reason = campo.trim();
          break;
        }
      }
    } catch {
      // corpo vazio ou não-JSON: cai no texto padrão do chamador
    }
    this.notifyInactiveBroker(response.status, reason);
    return reason;
  }

  async policyPdf(
    proposalId: string
  ): Promise<{ blob: Blob; filename: string }> {
    const path = `${BASE_PATH}/${encodeURIComponent(proposalId)}/policy-pdf`;
    const response = await this.get(path, {}, PORTAL_API_URL);
    if (!response.ok) {
      // O motivo real vem no corpo (`detail`): "só fica disponível depois
      // da emissão", "conversor de PDF não configurado", etc. Descartá-lo e
      // deixar o chamador adivinhar fazia o Portal mostrar "a apólice fica
      // disponível depois da emissão" numa apólice JÁ EMITIDA — o corretor
      // conclui que a emissão falhou (achado do QA de 2026-08-21).
      throw new Error(await this.reasonFromResponse(response));
    }
    const blob = await response.blob();
    const filename = filenameFromResponse(
      response,
      `apolice-rd-${proposalId}.pdf`
    );
    return { blob, filename };
  }

  /**
   * PDF de Cotação ou Proposta — `tipo` explícito, disponível em qualquer
   * status. Distinto de `policyPdf`: aquele alterna proposta/apólice pela
   * situação da proposta; este nunca alterna, é o corretor quem escolhe.
   */
  async documentPdf(
    proposalId: string,
    tipo: 'cotacao' | 'proposta'
  ): Promise<{ blob: Blob; filename: string }> {
    const path =
      `${BASE_PATH}/${encodeURIComponent(proposalId)}/document-pdf` +
      `?tipo=${encodeURIComponent(tipo)}`;
    const response = await this.get(path, {}, PORTAL_API_URL);
    if (!response.ok) {
      throw new Error(await this.reasonFromResponse(response));
    }
    const blob = await response.blob();
    const filename = filenameFromResponse(
      response,
      `${tipo}-rd-${proposalId}.pdf`
    );
    return { blob, filename };
  }
}

export const rdEquipamentosService = new RdEquipamentosService();
